import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Gavel, Plus, Loader2, Eye, Lock, Archive, Search, Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AgreementBody, fmtDate, typeLabel } from "@/components/legal/AgreementBody";
import { downloadAcceptancePdf } from "@/components/legal/downloadPdf";
import { useTabParam } from "@/lib/useTabParam";

const err = (e, fb) => toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || fb);
const STATUS_CHIP = { DRAFT: "pf-chip-lav", PUBLISHED: "pf-chip-cyan", RETIRED: "" };
const pad = (n) => String(n).padStart(2, "0");
// datetime-local works in local wall-clock time; convert to/from UTC ISO explicitly.
const toLocalInput = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function DraftForm({ knownTypes, initial, onSaved, onCancel }) {
  const [f, setF] = useState(initial ? { ...initial, effective_at: toLocalInput(initial.effective_at) } : {
    agreement_type: "MERCHANT_AGREEMENT", version: "", title: "", audience: "MERCHANT", locale: "en-CA",
    effective_at: toLocalInput(), document_plaintext: "", requires_acceptance: true,
    requires_reacceptance: false, change_summary: "", is_placeholder: false,
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const editing = !!initial?.agreement_id;
  const save = async () => {
    try {
      setBusy(true);
      const body = { ...f, effective_at: new Date(f.effective_at).toISOString() };
      const { data } = editing
        ? await api.patch(`/admin/legal/agreements/${initial.agreement_id}`, body)
        : await api.post("/admin/legal/agreements", body);
      toast.success(editing ? "Draft updated" : "Draft created");
      onSaved(data);
    } catch (e) { err(e, "Could not save draft"); } finally { setBusy(false); }
  };
  const inp = "pf-input px-3 py-2 text-sm w-full";
  return (
    <div className="space-y-3" data-testid="legal-draft-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-xs font-semibold text-slate-600">Type
          <input list="legal-types" className={inp} value={f.agreement_type} onChange={set("agreement_type")} disabled={editing} data-testid="legal-draft-type" />
          <datalist id="legal-types">{knownTypes.map((t) => <option key={t} value={t} />)}</datalist>
        </label>
        <label className="text-xs font-semibold text-slate-600">Version
          <input className={inp} value={f.version} onChange={set("version")} placeholder="2027.02.1" disabled={editing} data-testid="legal-draft-version" />
        </label>
        <label className="text-xs font-semibold text-slate-600">Title
          <input className={inp} value={f.title} onChange={set("title")} data-testid="legal-draft-title" />
        </label>
        <label className="text-xs font-semibold text-slate-600">Audience
          <select className={inp} value={f.audience} onChange={set("audience")} disabled={editing} data-testid="legal-draft-audience">
            <option>MERCHANT</option><option>SHOPPER</option><option>BOTH</option>
          </select>
        </label>
        <label className="text-xs font-semibold text-slate-600">Effective at
          <input type="datetime-local" className={inp} value={f.effective_at || ""} onChange={set("effective_at")} data-testid="legal-draft-effective" />
        </label>
        <div className="flex flex-col gap-1.5 text-xs font-semibold text-slate-700 justify-end">
          <label className="inline-flex gap-2 items-center"><input type="checkbox" checked={f.requires_acceptance} onChange={set("requires_acceptance")} data-testid="legal-draft-requires-acceptance" /> Requires acceptance</label>
          <label className="inline-flex gap-2 items-center"><input type="checkbox" checked={f.requires_reacceptance} onChange={set("requires_reacceptance")} data-testid="legal-draft-requires-reacceptance" /> Requires re-acceptance from existing users</label>
          <label className="inline-flex gap-2 items-center"><input type="checkbox" checked={f.is_placeholder} onChange={set("is_placeholder")} data-testid="legal-draft-placeholder" /> Placeholder (not final production legal content)</label>
        </div>
      </div>
      <label className="text-xs font-semibold text-slate-600 block">Change summary (shown as “What changed”)
        <input className={inp} value={f.change_summary || ""} onChange={set("change_summary")} data-testid="legal-draft-change-summary" />
      </label>
      <label className="text-xs font-semibold text-slate-600 block">Document text (use “## ” for headings, “- ” for bullets)
        <textarea className={`${inp} font-mono h-64`} value={f.document_plaintext} onChange={set("document_plaintext")} data-testid="legal-draft-body" />
      </label>
      <div className="flex gap-3">
        <button onClick={save} disabled={busy} className="pf-btn-primary px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2" data-testid="legal-draft-save">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save draft
        </button>
        <button onClick={onCancel} className="text-xs font-semibold text-slate-600 underline">Cancel</button>
      </div>
    </div>
  );
}

function AgreementDetail({ id, knownTypes, onChanged, onClose }) {
  const [ag, setAg] = useState(null);
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [publishOpen, setPublishOpen] = useState(false);
  const load = useCallback(() => api.get(`/admin/legal/agreements/${id}`).then((r) => setAg(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  if (!ag) return <Loader2 className="w-5 h-5 animate-spin text-slate-400" />;
  const act = async (fn, ok) => {
    try { await fn(); toast.success(ok); setPublishOpen(false); await load(); onChanged(); } catch (e) { err(e, "Action failed (super admin only)"); }
  };
  if (editing) return <DraftForm knownTypes={knownTypes} initial={ag} onSaved={() => { setEditing(false); load(); onChanged(); }} onCancel={() => setEditing(false)} />;
  return (
    <div data-testid="legal-agreement-detail">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`pf-chip ${STATUS_CHIP[ag.status]}`} data-testid="legal-detail-status">{ag.status}</span>
        <span className="text-xs text-slate-500">{ag.agreement_type} · {ag.audience} · {ag.locale} · v{ag.version} · effective {fmtDate(ag.effective_at)}</span>
      </div>
      {ag.document_hash_sha256 && <div className="text-[11px] font-mono text-slate-400 break-all mt-1" data-testid="legal-detail-hash">SHA-256 {ag.document_hash_sha256}</div>}
      <div className="flex flex-wrap gap-2 mt-3">
        {ag.status === "DRAFT" && <>
          <button onClick={async () => { try { await api.delete(`/admin/legal/agreements/${id}`); toast.success("Draft deleted"); onChanged(); onClose(); } catch (e) { err(e, "Could not delete draft"); } }} className="text-xs font-semibold text-red-700 underline" data-testid="legal-detail-delete">Delete draft</button>
          <button onClick={() => setEditing(true)} className="pf-btn-glass px-4 py-1.5 rounded-full text-xs font-semibold" data-testid="legal-detail-edit">Edit draft</button>
          <button onClick={() => setPublishOpen(true)} className="pf-btn-primary px-4 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1" data-testid="legal-detail-publish"><Lock className="w-3 h-3" /> Publish</button>
        </>}
        {ag.status === "PUBLISHED" && <>
          <span className="text-xs text-slate-500 inline-flex items-center gap-1" data-testid="legal-detail-immutable"><Lock className="w-3 h-3" /> Published versions cannot be edited — create a new version.</span>
          <button onClick={() => act(() => api.post(`/admin/legal/agreements/${id}/retire`), "Retired")} className="pf-btn-glass px-4 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1" data-testid="legal-detail-retire"><Archive className="w-3 h-3" /> Retire</button>
        </>}
        <button onClick={onClose} className="text-xs font-semibold text-slate-600 underline ml-auto">Close</button>
      </div>
      {ag.change_summary && <div className="mt-3 text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"><b>What changed:</b> {ag.change_summary}</div>}
      <div className="mt-4 border border-slate-200 rounded-xl p-5 max-h-[55vh] overflow-y-auto bg-white"><AgreementBody text={ag.document_plaintext} /></div>
      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent data-testid="legal-publish-dialog">
          <DialogHeader>
            <DialogTitle>Confirm publish</DialogTitle>
            <DialogDescription>
              You are publishing {typeLabel(ag.agreement_type)} version <b>{ag.version}</b>. Once published, this version cannot be edited.
              {ag.requires_reacceptance && " Existing users will be asked to re-accept."}
            </DialogDescription>
          </DialogHeader>
          <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={`Type ${ag.version}`} className="pf-input px-3 py-2 text-sm w-full" data-testid="legal-publish-confirm-input" />
          <button disabled={confirm.trim() !== ag.version} onClick={() => act(() => api.post(`/admin/legal/agreements/${id}/publish`, { confirm_version: confirm.trim() }), "Published")}
                  className="pf-btn-primary px-5 py-2 rounded-full text-sm font-semibold disabled:opacity-50" data-testid="legal-publish-confirm-btn">Confirm Publish</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AgreementsTab() {
  const [data, setData] = useState(null);
  const [creating, setCreating] = useState(false);
  const [sel, setSel] = useState(null);
  const load = useCallback(() => api.get("/admin/legal/agreements").then((r) => setData(r.data)).catch((e) => err(e, "Could not load")), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-slate-400" />;
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-600" data-testid="legal-admin-enforcement">Terms enforcement: <b>{data.enforcement_active ? "ON (preview)" : "OFF"}</b></span>
        <button onClick={() => { setCreating(true); setSel(null); }} className="pf-btn-primary px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1" data-testid="legal-new-draft-btn"><Plus className="w-3 h-3" /> New draft</button>
      </div>
      {creating && <div className="pf-card p-5"><DraftForm knownTypes={data.known_types} onSaved={(d) => { setCreating(false); setSel(d.agreement_id); load(); }} onCancel={() => setCreating(false)} /></div>}
      {sel && <div className="pf-card p-5"><AgreementDetail id={sel} knownTypes={data.known_types} onChanged={load} onClose={() => setSel(null)} /></div>}
      <div className="pf-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="legal-agreements-table">
          <thead className="text-left text-xs uppercase tracking-widest text-slate-500"><tr>
            <th className="px-4 py-3">Terms</th><th className="px-4 py-3">Terms version</th><th className="px-4 py-3">Audience</th><th className="px-4 py-3">Effective</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Re-accept</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>{data.items.map((a) => (
            <tr key={a.agreement_id} className="border-t border-slate-100" data-testid={`legal-agreement-row-${a.agreement_id}`}>
              <td className="px-4 py-3 font-medium">{a.title}{a.is_placeholder && <span className="ml-2 text-[10px] font-bold text-red-700">PLACEHOLDER</span>}</td>
              <td className="px-4 py-3 font-mono text-xs">{a.version}</td>
              <td className="px-4 py-3 text-xs" data-testid={`legal-agreement-audience-${a.agreement_id}`}>{{ MERCHANT: "Merchant", SHOPPER: "Shopper", BOTH: "Both" }[a.audience] || a.audience}</td>
              <td className="px-4 py-3 text-xs">{fmtDate(a.effective_at)}</td>
              <td className="px-4 py-3"><span className={`pf-chip ${STATUS_CHIP[a.status]}`}>{a.status}</span></td>
              <td className="px-4 py-3 text-xs">{a.requires_reacceptance ? "Yes" : "No"}</td>
              <td className="px-4 py-3"><button onClick={() => { setSel(a.agreement_id); setCreating(false); }} className="text-xs font-semibold underline inline-flex items-center gap-1" data-testid={`legal-agreement-open-${a.agreement_id}`}><Eye className="w-3 h-3" /> Open</button></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

function AcceptancesTab() {
  const [f, setF] = useState({ q: "", agreement_type: "", version: "", date_from: "", date_to: "" });
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const reqId = useRef(0);
  const search = useCallback(async () => {
    const mine = ++reqId.current;
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v).map(([k, v]) => [k, k.startsWith("date_") ? new Date(v).toISOString() : v]));
    try {
      const { data: res } = await api.get("/admin/legal/acceptances", { params });
      if (mine === reqId.current) setData(res);
    } catch (e) { if (mine === reqId.current) err(e, "Search failed"); }
  }, [f]);
  useEffect(() => { search(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const inp = "pf-input px-3 py-2 text-xs";
  return (
    <div className="space-y-4">
      <div className="pf-card p-4 flex flex-wrap gap-2 items-end">
        <input className={`${inp} w-64`} placeholder="Email, name, merchant, user or acceptance ID" value={f.q} onChange={set("q")} data-testid="legal-audit-q" />
        <input className={`${inp} w-48`} placeholder="Agreement type" value={f.agreement_type} onChange={set("agreement_type")} data-testid="legal-audit-type" />
        <input className={`${inp} w-28`} placeholder="Version" value={f.version} onChange={set("version")} data-testid="legal-audit-version" />
        <input type="date" className={inp} value={f.date_from} onChange={set("date_from")} data-testid="legal-audit-from" />
        <input type="date" className={inp} value={f.date_to} onChange={set("date_to")} data-testid="legal-audit-to" />
        <button onClick={search} className="pf-btn-primary px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1" data-testid="legal-audit-search"><Search className="w-3 h-3" /> Search</button>
      </div>
      <div className="text-xs text-slate-500" data-testid="legal-audit-total">{data ? `${data.total} acceptance record(s) · read-only` : ""}</div>
      <div className="pf-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="legal-audit-table">
          <thead className="text-left text-xs uppercase tracking-widest text-slate-500"><tr>
            <th className="px-4 py-3">Accepted (UTC)</th><th className="px-4 py-3">Terms</th><th className="px-4 py-3">Actor</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Merchant</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>{(data?.items || []).map((a) => (
            <tr key={a.acceptance_id} className="border-t border-slate-100" data-testid={`legal-audit-row-${a.acceptance_id}`}>
              <td className="px-4 py-3 text-xs">{fmtDate(a.accepted_at, true)}</td>
              <td className="px-4 py-3 text-xs">{a.agreement_title || typeLabel(a.agreement_type)} <span className="font-mono">{a.agreement_version}</span></td>
              <td className="px-4 py-3 text-xs">{a.actor_type}</td>
              <td className="px-4 py-3 text-xs">{a.accepted_by_email}</td>
              <td className="px-4 py-3 text-xs">{a.merchant_legal_name || "—"}</td>
              <td className="px-4 py-3 text-xs font-semibold"><button onClick={() => setDetail(a)} className="underline" data-testid={`legal-audit-view-${a.acceptance_id}`}>Details</button></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="legal-audit-detail">
          <DialogHeader><DialogTitle>Acceptance record</DialogTitle><DialogDescription>Evidence records are immutable and cannot be edited.</DialogDescription></DialogHeader>
          {detail && (
            <div className="max-h-[60vh] overflow-y-auto text-xs">
              {Object.entries(detail).map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 gap-2 py-1 border-b border-slate-100"><span className="font-semibold text-slate-600">{k}</span><span className="col-span-2 break-all font-mono">{v === null ? "—" : String(v)}</span></div>
              ))}
              <button onClick={() => downloadAcceptancePdf(detail.acceptance_id)} className="mt-3 pf-btn-glass px-4 py-2 rounded-full font-semibold inline-flex items-center gap-1" data-testid="legal-audit-pdf"><Download className="w-3 h-3" /> Download PDF</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AdminLegal() {
  const [legalTab, setLegalTab] = useTabParam("agreements", ["agreements", "acceptances"]);
  return (
    <div data-testid="admin-legal">
      <div className="pf-chip pf-chip-lav"><Gavel className="w-3 h-3" /> Terms</div>
      <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 text-slate-900">Terms &amp; Conditions administration</h2>
      <p className="text-sm text-slate-600 mt-1 mb-6">Versioned Terms & Conditions and Privacy Notice documents, with immutable terms acceptance records.</p>
      <Tabs value={legalTab} onValueChange={setLegalTab}>
        <TabsList>
          <TabsTrigger value="agreements" data-testid="legal-tab-agreements">Terms versions</TabsTrigger>
          <TabsTrigger value="acceptances" data-testid="legal-tab-acceptances">Terms acceptance audit</TabsTrigger>
        </TabsList>
        <TabsContent value="agreements" className="mt-5"><AgreementsTab /></TabsContent>
        <TabsContent value="acceptances" className="mt-5"><AcceptancesTab /></TabsContent>
      </Tabs>
    </div>
  );
}
